import { memo, useCallback, useEffect, useState } from 'react';
import { AuditLog, HistoryLog } from '../../../API/APIObjects';
import { HistoryActionType } from '../../../API/Enums';
import { useGetHistoryActionTypeLabel } from '../../../API/Labels/Labels';
import { nameof } from '../../../CommonUtils/Nameof';
import { useAPICall } from '../../../Contexts/Axios';
import { useLocalization } from '../../../Contexts/Localization';
import { useSetMessage } from '../../../Contexts/Message';
import { GMLoadingOverlay } from '../../LoadingOverlay/LoadingOverlay';
import { ColumnType, GMTable } from '..';

interface ComponentProps {
    /**
     * API url to fetch HistoryLog data
     */
    queryUrl: string;
    /**
     * Unique name for the table. Used for unique keys
     */
    name: string;
}

export const HistoryTable: React.FC<ComponentProps> = memo((props) => {
    const Localization = useLocalization();
    const [loading, setLoading] = useState<boolean>(false);
    const [historyRows, setHistoryRows] = useState<AuditLog[] | undefined>(undefined);
    const api = useAPICall();
    const setMessage = useSetMessage();
    const getHistoryActionTypeLabel = useGetHistoryActionTypeLabel();

    const getActionArray = useCallback((action: HistoryActionType, fields: string[], changedFor: string, changedFrom: string[], changedTo: string[], changedIn: string) => {
        const actionArray: string[] = [];
        const actionLabel: string = getHistoryActionTypeLabel(action);

        if (fields && fields.length > 0) {
            fields.forEach((field: string, index: number) => {
                let actionString: string = `${actionLabel ? actionLabel + ' ' : ''}${field}`;

                if (changedFor) {
                    actionString = `${actionString} ${Localization.for_IGNORE} ${changedFor}`;
                }

                if (changedFrom && changedFrom[index] !== undefined) {
                    actionString = `${actionString} ${Localization.from} ${changedFrom[index] ? changedFrom[index] : Localization.blank}`;
                }

                if (changedTo && changedTo[index] !== undefined) {
                    actionString = `${actionString} ${Localization.to} ${changedTo[index] ? changedTo[index] : Localization.blank}`;
                }

                if (changedIn) {
                    actionString = `${actionString} ${Localization.in_IGNORE} ${changedIn}`;
                }

                actionArray.push(actionString);
            });
        }
        else {
            let actionString: string = actionLabel;

            if (changedIn) {
                actionString = `${actionString} (${Localization.formatString(Localization.CopiedFrom_IGNORE, changedIn) as string})`;
            }

            actionArray.push(actionString);
        }

        return actionArray;
    }, [getHistoryActionTypeLabel, Localization]);

    useEffect(() => {
        api<HistoryLog[]>(
            (history) => {
                setHistoryRows(
                    history.map((entry) => {
                        return {
                            timestamp: entry.timestamp,
                            action: getActionArray(entry.action, entry.fields, entry.changedFor, entry.changedFrom, entry.changedTo, entry.changedIn),
                            user: entry.displayName ?? entry.user,
                        };
                    }),
                );
            },
            setLoading,
            (error) => setMessage({
                title: Localization.formatString(Localization.FailedToFetch, Localization.AuditTrail) as string,
                message: error,
            }),
        )
            .get(props.queryUrl);

    }, [api, setMessage, Localization, props.queryUrl, getActionArray]);

    return (
        <GMLoadingOverlay loading={loading}>
            <GMTable
                name={`${props.name}-history`}
                columns={[
                    {
                        id: 1,
                        label: Localization.DateTime,
                        rowDataKey: nameof<HistoryLog>('timestamp'),
                        percentWidth: 30,
                        type: ColumnType.Date,
                    },
                    {
                        id: 2,
                        label: Localization.Event,
                        rowDataKey: nameof<HistoryLog>('action'),
                        percentWidth: 50,
                        type: ColumnType.ListMultiLine,
                    },
                    {
                        id: 3,
                        label: Localization.User,
                        rowDataKey: nameof<HistoryLog>('user'),
                        percentWidth: 20,
                    },
                ]}
                rows={historyRows}
            />
        </GMLoadingOverlay>
    );
});